/**
 * Layered (Sugiyama-style) layout for directed graphs — flowcharts and concept maps.
 *
 * The pipeline is the classic one, kept deterministic so the same text always draws the
 * same picture on the server, in the browser and in the tests:
 *
 * 1. **Cycle removal.** A depth-first search from the sources marks the edges that close a
 *    cycle; those are laid out reversed and drawn as loops back up the page.
 * 2. **Layering.** Longest-path layering with a minimum edge length (2 for an edge that
 *    carries a label, so the label has a layer of its own), then sources are pulled down
 *    next to their successors so no edge is longer than it has to be.
 * 3. **Dummy vertices.** An edge spanning several layers becomes a chain of zero-height
 *    wire vertices, and an edge label becomes a box vertex in the chain. Because both take
 *    part in ordering and placement like real nodes, a line or a label can never land on
 *    top of a node.
 * 4. **Ordering.** Barycenter sweeps down and up, plus adjacent transpositions, keep the
 *    fewest crossings found.
 * 5. **Horizontal placement.** Alternating sweeps place each vertex at the weighted mean
 *    of its neighbours, and each layer is then projected onto the nearest arrangement that
 *    respects the minimum separation (isotonic regression — exact, and it preserves the
 *    order), so boxes never overlap.
 * 6. **Routing.** Orthogonal: down from the source, across on a track in the gap between
 *    two layers, down into the target. Edges leaving one node share a track (a bus);
 *    otherwise overlapping runs get separate tracks. A reversed edge leaves and enters
 *    on the right-hand side of its two nodes, through a stub, so it cannot be mistaken
 *    for an edge arriving at the top.
 *
 * Everything is laid out top-to-bottom; `direction: "right"` swaps width and height going
 * in and transposes the result coming out.
 */

import type { Rect } from "@/lib/tool-kit/charts/layout";

export type LayeredDirection = "down" | "right";

export interface LayeredNodeInput {
  id: string;
  width: number;
  height: number;
}

export interface LayeredEdgeInput {
  from: string;
  to: string;
  /** Size of the label box sitting on the edge, if the edge has one. */
  label?: { width: number; height: number } | null;
}

export interface LayeredOptions {
  direction?: LayeredDirection;
  /** Minimum space between boxes in one layer. */
  nodeGap?: number;
  /** Space between layers, before any extra for edge tracks. */
  rankGap?: number;
}

export type Point = [number, number];

export interface PlacedNode extends Rect {
  id: string;
  layer: number;
}

export interface PlacedEdge {
  /** Index into the edge list passed in. */
  index: number;
  from: string;
  to: string;
  /** From `from` to `to`; the arrowhead belongs at the last point. */
  points: Point[];
  label: Rect | null;
  /** True for an edge drawn as a loop back up the page. */
  reversed: boolean;
}

export interface LayeredLayout {
  width: number;
  height: number;
  nodes: PlacedNode[];
  edges: PlacedEdge[];
  layers: number;
}

const MARGIN = 16;
const WIRE_WIDTH = 4;
const STUB_START = 12;
const STUB_STEP = 8;
const TRACK_STEP = 9;
const SWEEPS = 24;
/** Offsets up to this many units are drawn straight rather than as a jog. */
const SNAP = 3;

interface Size {
  width: number;
  height: number;
}

interface Prepared {
  index: number;
  u: number;
  v: number;
  label: Size | null;
}

interface Vertex {
  layer: number;
  width: number;
  height: number;
  /** Input node index; -1 for wires and labels. */
  node: number;
  label: boolean;
  ups: number[];
  downs: number[];
  order: number;
  x: number;
  stubs: number;
}

interface Link {
  from: number;
  to: number;
  startStub: number | null;
  endStub: number | null;
  track: number;
}

interface Chain {
  edge: Prepared;
  reversed: boolean;
  vertices: number[];
  links: Link[];
  label: number | null;
}

/* ---------------------------------------------------------- input cleaning */

function swapped(size: Size, direction: LayeredDirection): Size {
  return direction === "right"
    ? { width: size.height, height: size.width }
    : size;
}

/** Drop edges with unknown ends, self-loops and repeats of an earlier edge. */
function prepareEdges(
  ids: Map<string, number>,
  edges: readonly LayeredEdgeInput[],
  direction: LayeredDirection
): Prepared[] {
  const seen = new Set<string>();
  const prepared: Prepared[] = [];
  for (const [index, edge] of edges.entries()) {
    const u = ids.get(edge.from);
    const v = ids.get(edge.to);
    if (u === undefined || v === undefined || u === v) {
      continue;
    }
    const key = `${u}>${v}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    prepared.push({
      index,
      u,
      v,
      label: edge.label ? swapped(edge.label, direction) : null,
    });
  }
  return prepared;
}

/* ---------------------------------------------------------- cycles, layers */

/** Which edges close a cycle in a depth-first search from the sources. */
function findReversed(count: number, edges: readonly Prepared[]): boolean[] {
  const out: number[][] = Array.from({ length: count }, () => []);
  const indegree = new Array<number>(count).fill(0);
  for (const [i, edge] of edges.entries()) {
    out[edge.u].push(i);
    indegree[edge.v] += 1;
  }
  const state = new Array<number>(count).fill(0);
  const reversed = new Array<boolean>(edges.length).fill(false);
  const visit = (u: number) => {
    state[u] = 1;
    for (const i of out[u]) {
      const v = edges[i].v;
      if (state[v] === 1) {
        reversed[i] = true;
      } else if (state[v] === 0) {
        visit(v);
      }
    }
    state[u] = 2;
  };
  const starts = [
    ...indegree.flatMap((degree, node) => (degree === 0 ? [node] : [])),
    ...Array.from({ length: count }, (_, node) => node),
  ];
  for (const start of starts) {
    if (state[start] === 0) {
      visit(start);
    }
  }
  return reversed;
}

interface Directed {
  a: number;
  b: number;
  minLength: number;
}

/** Longest-path layers, then sources pulled down beside their successors. */
function assignLayers(count: number, dag: readonly Directed[]): number[] {
  const out: Directed[][] = Array.from({ length: count }, () => []);
  const indegree = new Array<number>(count).fill(0);
  for (const edge of dag) {
    out[edge.a].push(edge);
    indegree[edge.b] += 1;
  }
  const layer = new Array<number>(count).fill(0);
  const remaining = [...indegree];
  const queue = remaining.flatMap((degree, node) =>
    degree === 0 ? [node] : []
  );
  for (const u of queue) {
    for (const edge of out[u]) {
      layer[edge.b] = Math.max(layer[edge.b], layer[u] + edge.minLength);
      remaining[edge.b] -= 1;
      if (remaining[edge.b] === 0) {
        queue.push(edge.b);
      }
    }
  }
  for (let node = 0; node < count; node++) {
    if (indegree[node] === 0 && out[node].length > 0) {
      layer[node] = Math.min(
        ...out[node].map((edge) => layer[edge.b] - edge.minLength)
      );
    }
  }
  return layer;
}

/* ------------------------------------------------------------ the graph */

function newVertex(
  layer: number,
  size: Size,
  node: number,
  label: boolean
): Vertex {
  return {
    layer,
    width: size.width,
    height: size.height,
    node,
    label,
    ups: [],
    downs: [],
    order: 0,
    x: 0,
    stubs: 0,
  };
}

function addStubs(
  vertices: Vertex[],
  chain: Chain,
  top: number,
  bottom: number
): void {
  const first = chain.links[0];
  const last = chain.links.at(-1) as Link;
  first.startStub = STUB_START + STUB_STEP * vertices[top].stubs;
  vertices[top].stubs += 1;
  last.endStub = STUB_START + STUB_STEP * vertices[bottom].stubs;
  vertices[bottom].stubs += 1;
}

/** The wires and the label box between `top` and `bottom`, top to bottom. */
function addWires(
  vertices: Vertex[],
  chain: Chain,
  top: number,
  span: number
): void {
  const edge = chain.edge;
  const labelAt = edge.label ? Math.floor((span - 2) / 2) : -1;
  for (let step = 0; step < span - 1; step++) {
    const isLabel = step === labelAt && edge.label !== null;
    const size =
      isLabel && edge.label ? edge.label : { width: WIRE_WIDTH, height: 0 };
    vertices.push(newVertex(vertices[top].layer + step + 1, size, -1, isLabel));
    chain.vertices.push(vertices.length - 1);
    if (isLabel) {
      chain.label = vertices.length - 1;
    }
  }
}

function linkChain(vertices: Vertex[], chain: Chain): void {
  for (let k = 0; k + 1 < chain.vertices.length; k++) {
    const from = chain.vertices[k];
    const to = chain.vertices[k + 1];
    vertices[from].downs.push(to);
    vertices[to].ups.push(from);
    chain.links.push({ from, to, startStub: null, endStub: null, track: -1 });
  }
}

/** Real vertices first (in node order), then each edge's chain of wires and label. */
function buildChains(
  vertices: Vertex[],
  prepared: readonly Prepared[],
  reversed: readonly boolean[]
): Chain[] {
  const chains: Chain[] = [];
  for (const [i, edge] of prepared.entries()) {
    const flip = reversed[i];
    const top = flip ? edge.v : edge.u;
    const bottom = flip ? edge.u : edge.v;
    const chain: Chain = {
      edge,
      reversed: flip,
      vertices: [top],
      links: [],
      label: null,
    };
    addWires(
      vertices,
      chain,
      top,
      vertices[bottom].layer - vertices[top].layer
    );
    chain.vertices.push(bottom);
    linkChain(vertices, chain);
    if (flip) {
      addStubs(vertices, chain, top, bottom);
    }
    chains.push(chain);
  }
  return chains;
}

/* ------------------------------------------------------------- ordering */

function initialLayers(vertices: Vertex[], layerCount: number): number[][] {
  const rank = new Array<number>(vertices.length).fill(-1);
  let counter = 0;
  const visit = (v: number) => {
    rank[v] = counter++;
    for (const next of vertices[v].downs) {
      if (rank[next] < 0) {
        visit(next);
      }
    }
  };
  const roots = vertices.flatMap((vertex, v) =>
    vertex.ups.length === 0 ? [v] : []
  );
  for (const v of [...roots, ...vertices.keys()]) {
    if (rank[v] < 0) {
      visit(v);
    }
  }
  const layers: number[][] = Array.from({ length: layerCount }, () => []);
  for (const [v, vertex] of vertices.entries()) {
    layers[vertex.layer].push(v);
  }
  for (const layer of layers) {
    layer.sort((a, b) => rank[a] - rank[b]);
  }
  return layers;
}

function setOrders(vertices: Vertex[], layers: readonly number[][]): void {
  for (const layer of layers) {
    for (const [position, v] of layer.entries()) {
      vertices[v].order = position;
    }
  }
}

function crossingsBelow(vertices: Vertex[], upper: readonly number[]): number {
  const pairs: Point[] = [];
  for (const u of upper) {
    for (const d of vertices[u].downs) {
      pairs.push([vertices[u].order, vertices[d].order]);
    }
  }
  let count = 0;
  for (let i = 0; i < pairs.length; i++) {
    for (let j = i + 1; j < pairs.length; j++) {
      if ((pairs[i][0] - pairs[j][0]) * (pairs[i][1] - pairs[j][1]) < 0) {
        count += 1;
      }
    }
  }
  return count;
}

function totalCrossings(
  vertices: Vertex[],
  layers: readonly number[][]
): number {
  let total = 0;
  for (let i = 0; i + 1 < layers.length; i++) {
    total += crossingsBelow(vertices, layers[i]);
  }
  return total;
}

function barycenterSort(
  vertices: Vertex[],
  layer: number[],
  useUps: boolean
): void {
  const keyed = layer.map((v, position) => {
    const refs = useUps ? vertices[v].ups : vertices[v].downs;
    const bary =
      refs.length > 0
        ? refs.reduce((sum, r) => sum + vertices[r].order, 0) / refs.length
        : position;
    return { v, bary, position };
  });
  keyed.sort((a, b) => a.bary - b.bary || a.position - b.position);
  for (const [position, entry] of keyed.entries()) {
    layer[position] = entry.v;
    vertices[entry.v].order = position;
  }
}

function pairCrossings(vertices: Vertex[], a: number, b: number): number {
  let count = 0;
  const sides: [number[], number[]][] = [
    [vertices[a].ups, vertices[b].ups],
    [vertices[a].downs, vertices[b].downs],
  ];
  for (const [left, right] of sides) {
    for (const x of left) {
      for (const y of right) {
        if (vertices[x].order > vertices[y].order) {
          count += 1;
        }
      }
    }
  }
  return count;
}

function transposeLayer(vertices: Vertex[], layer: number[]): boolean {
  let improved = false;
  for (let i = 0; i + 1 < layer.length; i++) {
    const a = layer[i];
    const b = layer[i + 1];
    if (pairCrossings(vertices, b, a) < pairCrossings(vertices, a, b)) {
      layer[i] = b;
      layer[i + 1] = a;
      vertices[a].order = i + 1;
      vertices[b].order = i;
      improved = true;
    }
  }
  return improved;
}

function transposeAll(vertices: Vertex[], layers: number[][]): void {
  for (let pass = 0; pass < 4; pass++) {
    let improved = false;
    for (const layer of layers) {
      improved = transposeLayer(vertices, layer) || improved;
    }
    if (!improved) {
      return;
    }
  }
}

function minimizeCrossings(vertices: Vertex[], layers: number[][]): void {
  setOrders(vertices, layers);
  let best = layers.map((layer) => [...layer]);
  let bestCount = totalCrossings(vertices, layers);
  for (let sweep = 0; sweep < SWEEPS && bestCount > 0; sweep++) {
    for (let i = 1; i < layers.length; i++) {
      barycenterSort(vertices, layers[i], true);
    }
    for (let i = layers.length - 2; i >= 0; i--) {
      barycenterSort(vertices, layers[i], false);
    }
    transposeAll(vertices, layers);
    const count = totalCrossings(vertices, layers);
    if (count < bestCount) {
      bestCount = count;
      best = layers.map((layer) => [...layer]);
    }
  }
  for (const [i, layer] of best.entries()) {
    layers[i] = layer;
  }
  setOrders(vertices, layers);
}

/* ------------------------------------------------- horizontal placement */

function gapBetween(a: Vertex, b: Vertex, nodeGap: number): number {
  const wireA = a.node < 0 && !a.label;
  const wireB = b.node < 0 && !b.label;
  let gap = nodeGap;
  if (wireA && wireB) {
    gap = 10;
  } else if (wireA || wireB) {
    gap = 14;
  } else if (a.label || b.label) {
    gap = 16;
  }
  const reserve = a.stubs > 0 ? STUB_START + STUB_STEP * a.stubs + 10 : 0;
  return Math.max(gap, reserve);
}

function centerDistance(a: Vertex, b: Vertex, nodeGap: number): number {
  return (a.width + b.width) / 2 + gapBetween(a, b, nodeGap);
}

interface Block {
  sum: number;
  weight: number;
  count: number;
}

/** Weighted least-squares fit of a non-decreasing sequence (pool adjacent violators). */
export function isotonicFit(
  values: readonly number[],
  weights: readonly number[]
): number[] {
  const blocks: Block[] = [];
  for (const [i, value] of values.entries()) {
    blocks.push({ sum: value * weights[i], weight: weights[i], count: 1 });
    while (blocks.length > 1) {
      const last = blocks.at(-1) as Block;
      const previous = blocks.at(-2) as Block;
      if (previous.sum / previous.weight <= last.sum / last.weight) {
        break;
      }
      blocks.splice(blocks.length - 2, 2, {
        sum: previous.sum + last.sum,
        weight: previous.weight + last.weight,
        count: previous.count + last.count,
      });
    }
  }
  const fitted: number[] = [];
  for (const block of blocks) {
    for (let k = 0; k < block.count; k++) {
      fitted.push(block.sum / block.weight);
    }
  }
  return fitted;
}

type Reference = "ups" | "downs" | "both";

function referencesOf(vertex: Vertex, reference: Reference): number[] {
  if (reference === "ups") {
    return vertex.ups;
  }
  if (reference === "downs") {
    return vertex.downs;
  }
  return [...vertex.ups, ...vertex.downs];
}

function placeLayer(
  vertices: Vertex[],
  layer: readonly number[],
  reference: Reference,
  nodeGap: number
): void {
  const offsets: number[] = [];
  let run = 0;
  for (const [i, v] of layer.entries()) {
    if (i > 0) {
      run += centerDistance(vertices[layer[i - 1]], vertices[v], nodeGap);
    }
    offsets.push(run);
  }
  const wanted = layer.map((v, i) => {
    const refs = referencesOf(vertices[v], reference);
    const target =
      refs.length > 0
        ? refs.reduce((sum, r) => sum + vertices[r].x, 0) / refs.length
        : vertices[v].x;
    return target - offsets[i];
  });
  const weights = layer.map((v) =>
    Math.max(1, referencesOf(vertices[v], reference).length)
  );
  const fitted = isotonicFit(wanted, weights);
  for (const [i, v] of layer.entries()) {
    vertices[v].x = fitted[i] + offsets[i];
  }
}

function assignX(
  vertices: Vertex[],
  layers: readonly number[][],
  nodeGap: number
): void {
  for (const layer of layers) {
    let run = 0;
    for (const [i, v] of layer.entries()) {
      if (i > 0) {
        run += centerDistance(vertices[layer[i - 1]], vertices[v], nodeGap);
      }
      vertices[v].x = run;
    }
    for (const v of layer) {
      vertices[v].x -= run / 2;
    }
  }
  for (let round = 0; round < 6; round++) {
    for (let i = 1; i < layers.length; i++) {
      placeLayer(vertices, layers[i], "ups", nodeGap);
    }
    for (let i = layers.length - 2; i >= 0; i--) {
      placeLayer(vertices, layers[i], "downs", nodeGap);
    }
  }
  for (let round = 0; round < 4; round++) {
    for (const layer of layers) {
      placeLayer(vertices, layer, "both", nodeGap);
    }
  }
}

/* --------------------------------------------------- vertical placement */

interface Frame {
  bandTop: number[];
  bandHeight: number[];
  gapTop: number[];
  gapHeight: number[];
  trackCount: number[];
}

function bandHeights(vertices: Vertex[], layerCount: number): number[] {
  const heights = new Array<number>(layerCount).fill(0);
  for (const vertex of vertices) {
    heights[vertex.layer] = Math.max(heights[vertex.layer], vertex.height);
  }
  return heights;
}

interface LinkGeometry {
  startX: number;
  endX: number;
}

function linkGeometry(vertices: Vertex[], link: Link): LinkGeometry {
  const a = vertices[link.from];
  const b = vertices[link.to];
  const startX =
    link.startStub === null ? a.x : a.x + a.width / 2 + link.startStub;
  if (link.endStub !== null) {
    return { startX, endX: b.x + b.width / 2 + link.endStub };
  }
  const straight = link.startStub === null && Math.abs(b.x - startX) <= SNAP;
  return { startX, endX: straight ? startX : b.x };
}

/** Pull wires and labels onto the line above them when they are only a hair off. */
function snapChains(vertices: Vertex[], chains: readonly Chain[]): void {
  for (const chain of chains) {
    for (let k = 1; k + 1 < chain.vertices.length; k++) {
      const previous = vertices[chain.vertices[k - 1]];
      const vertex = vertices[chain.vertices[k]];
      if (Math.abs(vertex.x - previous.x) <= SNAP) {
        vertex.x = previous.x;
      }
    }
  }
}

interface TrackGroup {
  lo: number;
  hi: number;
  links: Link[];
}

/** Give every non-straight link a track in its gap; returns tracks used per gap. */
function assignTracks(
  vertices: Vertex[],
  chains: readonly Chain[],
  layerCount: number
): number[] {
  const groups: Map<string, TrackGroup>[] = Array.from(
    { length: layerCount },
    () => new Map()
  );
  for (const [c, chain] of chains.entries()) {
    for (const link of chain.links) {
      const { startX, endX } = linkGeometry(vertices, link);
      if (Math.abs(startX - endX) <= 0.01) {
        continue;
      }
      const gap = vertices[link.from].layer;
      const key =
        link.startStub === null ? `v${link.from}` : `e${c}:${link.from}`;
      const group = groups[gap].get(key) ?? {
        lo: Number.POSITIVE_INFINITY,
        hi: Number.NEGATIVE_INFINITY,
        links: [],
      };
      group.lo = Math.min(group.lo, startX, endX);
      group.hi = Math.max(group.hi, startX, endX);
      group.links.push(link);
      groups[gap].set(key, group);
    }
  }
  return groups.map((byKey) => {
    const ordered = [...byKey.values()].sort((a, b) => a.lo - b.lo);
    const ends: number[] = [];
    for (const group of ordered) {
      let track = ends.findIndex((end) => end + 6 < group.lo);
      if (track < 0) {
        track = ends.length;
        ends.push(group.hi);
      } else {
        ends[track] = group.hi;
      }
      for (const link of group.links) {
        link.track = track;
      }
    }
    return ends.length;
  });
}

function buildFrame(
  vertices: Vertex[],
  chains: readonly Chain[],
  layerCount: number,
  rankGap: number
): Frame {
  const bandHeight = bandHeights(vertices, layerCount);
  const trackCount = assignTracks(vertices, chains, layerCount);
  const bandTop: number[] = [];
  const gapTop: number[] = [];
  const gapHeight: number[] = [];
  let y = MARGIN;
  for (let i = 0; i < layerCount; i++) {
    bandTop.push(y);
    y += bandHeight[i];
    gapTop.push(y);
    const height = rankGap + Math.max(0, trackCount[i] - 1) * TRACK_STEP;
    gapHeight.push(height);
    y += i + 1 < layerCount ? height : 0;
  }
  return { bandTop, bandHeight, gapTop, gapHeight, trackCount };
}

/* ----------------------------------------------------------- routing */

function midY(frame: Frame, vertex: Vertex): number {
  return frame.bandTop[vertex.layer] + frame.bandHeight[vertex.layer] / 2;
}

function topY(frame: Frame, vertex: Vertex): number {
  return vertex.node >= 0
    ? midY(frame, vertex) - vertex.height / 2
    : frame.bandTop[vertex.layer];
}

function bottomY(frame: Frame, vertex: Vertex): number {
  return vertex.node >= 0
    ? midY(frame, vertex) + vertex.height / 2
    : frame.bandTop[vertex.layer] + frame.bandHeight[vertex.layer];
}

function trackY(frame: Frame, gap: number, track: number): number {
  const count = frame.trackCount[gap];
  return (
    frame.gapTop[gap] +
    frame.gapHeight[gap] / 2 +
    (track - (count - 1) / 2) * TRACK_STEP
  );
}

function simplify(points: Point[]): Point[] {
  const result: Point[] = [];
  for (const point of points) {
    const last = result.at(-1);
    if (
      last &&
      Math.abs(last[0] - point[0]) + Math.abs(last[1] - point[1]) < 0.01
    ) {
      continue;
    }
    result.push(point);
  }
  let i = 1;
  while (i < result.length - 1) {
    const [a, b, c] = [result[i - 1], result[i], result[i + 1]];
    const flatX = Math.abs(a[0] - b[0]) < 0.01 && Math.abs(b[0] - c[0]) < 0.01;
    const flatY = Math.abs(a[1] - b[1]) < 0.01 && Math.abs(b[1] - c[1]) < 0.01;
    if (flatX || flatY) {
      result.splice(i, 1);
    } else {
      i += 1;
    }
  }
  return result;
}

function routeChain(vertices: Vertex[], frame: Frame, chain: Chain): Point[] {
  const points: Point[] = [];
  for (const [k, link] of chain.links.entries()) {
    const a = vertices[link.from];
    const b = vertices[link.to];
    const { startX, endX } = linkGeometry(vertices, link);
    if (k === 0 && link.startStub !== null) {
      points.push([a.x + a.width / 2, midY(frame, a)]);
    }
    const startY = link.startStub === null ? bottomY(frame, a) : midY(frame, a);
    const endY = link.endStub === null ? topY(frame, b) : midY(frame, b);
    points.push([startX, startY]);
    if (link.track >= 0) {
      const y = trackY(frame, a.layer, link.track);
      points.push([startX, y], [endX, y]);
    }
    points.push([endX, endY]);
    if (k === chain.links.length - 1 && link.endStub !== null) {
      points.push([b.x + b.width / 2, midY(frame, b)]);
    }
  }
  const clean = simplify(points);
  return chain.reversed ? clean.reverse() : clean;
}

/* ------------------------------------------------------------- output */

function transposeRect(rect: Rect): Rect {
  return { x: rect.y, y: rect.x, width: rect.height, height: rect.width };
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function roundRect(rect: Rect): Rect {
  return {
    x: round1(rect.x),
    y: round1(rect.y),
    width: round1(rect.width),
    height: round1(rect.height),
  };
}

/** Shift everything so the leftmost thing sits at MARGIN, and measure the extent. */
function normalize(
  nodes: PlacedNode[],
  edges: PlacedEdge[]
): { width: number; height: number } {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const rect of [
    ...nodes,
    ...edges.flatMap((edge) => (edge.label ? [edge.label] : [])),
  ]) {
    xs.push(rect.x, rect.x + rect.width);
    ys.push(rect.y, rect.y + rect.height);
  }
  for (const edge of edges) {
    for (const [x, y] of edge.points) {
      xs.push(x);
      ys.push(y);
    }
  }
  const shiftX = MARGIN - Math.min(...xs);
  const shiftY = MARGIN - Math.min(...ys);
  for (const node of nodes) {
    node.x += shiftX;
    node.y += shiftY;
  }
  for (const edge of edges) {
    edge.points = edge.points.map(([x, y]) => [x + shiftX, y + shiftY]);
    if (edge.label) {
      edge.label = {
        ...edge.label,
        x: edge.label.x + shiftX,
        y: edge.label.y + shiftY,
      };
    }
  }
  return {
    width: Math.max(...xs) + shiftX + MARGIN,
    height: Math.max(...ys) + shiftY + MARGIN,
  };
}

/**
 * Lay out `nodes` and the directed `edges` between them.
 *
 * Self-loops, edges to unknown ids and repeats of an earlier edge are skipped; the caller
 * reports those. Node ids must be unique.
 */
export function layoutLayered(
  nodes: readonly LayeredNodeInput[],
  edges: readonly LayeredEdgeInput[],
  options: LayeredOptions = {}
): LayeredLayout {
  const direction = options.direction ?? "down";
  const nodeGap = options.nodeGap ?? (direction === "down" ? 36 : 26);
  const rankGap = options.rankGap ?? (direction === "down" ? 34 : 52);
  if (nodes.length === 0) {
    return {
      width: 2 * MARGIN,
      height: 2 * MARGIN,
      nodes: [],
      edges: [],
      layers: 0,
    };
  }
  const ids = new Map(nodes.map((node, i) => [node.id, i]));
  const prepared = prepareEdges(ids, edges, direction);
  const reversed = findReversed(nodes.length, prepared);
  const dag: Directed[] = prepared.map((edge, i) => ({
    a: reversed[i] ? edge.v : edge.u,
    b: reversed[i] ? edge.u : edge.v,
    minLength: edge.label ? 2 : 1,
  }));
  const layerOf = assignLayers(nodes.length, dag);
  const layerCount = Math.max(...layerOf) + 1;
  const vertices = nodes.map((node, i) =>
    newVertex(layerOf[i], swapped(node, direction), i, false)
  );
  const chains = buildChains(vertices, prepared, reversed);
  const layers = initialLayers(vertices, layerCount);
  minimizeCrossings(vertices, layers);
  assignX(vertices, layers, nodeGap);
  snapChains(vertices, chains);
  const frame = buildFrame(vertices, chains, layerCount, rankGap);

  const placedNodes: PlacedNode[] = nodes.map((node, i) => {
    const vertex = vertices[i];
    return {
      id: node.id,
      layer: vertex.layer,
      x: vertex.x - vertex.width / 2,
      y: midY(frame, vertex) - vertex.height / 2,
      width: vertex.width,
      height: vertex.height,
    };
  });
  const placedEdges: PlacedEdge[] = chains.map((chain) => {
    const label = chain.label === null ? null : vertices[chain.label];
    return {
      index: chain.edge.index,
      from: nodes[chain.edge.u].id,
      to: nodes[chain.edge.v].id,
      points: routeChain(vertices, frame, chain),
      label: label
        ? {
            x: label.x - label.width / 2,
            y: midY(frame, label) - label.height / 2,
            width: label.width,
            height: label.height,
          }
        : null,
      reversed: chain.reversed,
    };
  });
  const flip = direction === "right";
  if (flip) {
    for (const node of placedNodes) {
      Object.assign(node, transposeRect(node));
    }
    for (const edge of placedEdges) {
      edge.points = edge.points.map(([x, y]): Point => [y, x]);
      edge.label = edge.label ? transposeRect(edge.label) : null;
    }
  }
  const size = normalize(placedNodes, placedEdges);
  for (const node of placedNodes) {
    Object.assign(node, roundRect(node));
  }
  for (const edge of placedEdges) {
    edge.points = edge.points.map(([x, y]): Point => [round1(x), round1(y)]);
    edge.label = edge.label ? roundRect(edge.label) : null;
  }
  return {
    width: Math.ceil(size.width),
    height: Math.ceil(size.height),
    nodes: placedNodes,
    edges: placedEdges,
    layers: layerCount,
  };
}
